import { useEffect, useRef } from "react";
import type { RefObject } from "react";

/** Why the surface is being dismissed — callers usually restore focus only for `escape`. */
export type DismissReason = "outside" | "escape";

/**
 * Close an open, trigger-anchored surface when the user points somewhere else or presses
 * Escape.
 *
 * Listens for `pointerdown` rather than `click` so the surface is gone before the click lands
 * on whatever sits underneath it. Both listeners are attached only while `isOpen`.
 */
export function useDismissOnOutside(
  containerRef: RefObject<HTMLElement>,
  isOpen: boolean,
  onDismiss: (reason: DismissReason) => void,
): void {
  // Held in a ref so an inline callback does not tear down and re-attach the listeners on
  // every render of the calling component.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const container = containerRef.current;
      if (container && !container.contains(event.target as Node)) {
        onDismissRef.current("outside");
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismissRef.current("escape");
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [containerRef, isOpen]);
}
